// Live View - SSE and real-time event handling

import type { LiveEvent, LiveSession } from './types';
import { liveState, appState } from './state';
import { formatTimeSince, setHTML, setText, formatPrice, parsePrice } from './utils';

const eventEmojis: Record<string, string> = {
  'page_view': '📄',
  'product_viewed': '👁️',
  'add_to_cart': '🛒',
  'cart_viewed': '🛍️',
  'checkout_initiated': '💳',
  'purchase_completed': '✅',
  'chatbot_opened': '💬',
  'chatbot_closed': '❌',
  'chat_message': '💭',
  'chat_message_sent': '👤',
  'chat_message_received': '🤖',
  'chat_started': '🎯',
  'product_recommendation_shown': '🎁',
  'search_no_results': '🔍',
  'product_alternatives_shown': '🔄'
};

export function connectLiveStream(): void {
  const clientId = appState.selectedClientId;
  
  if (!clientId) {
    updateConnectionStatus('disconnected', 'Select a client');
    return;
  }
  
  // Close existing connection
  if (liveState.eventSource) {
    liveState.eventSource.close();
  }
  
  updateConnectionStatus('connecting', 'Connecting...');
  
  const token = localStorage.getItem('analytics_auth_token') || '';
  const eventSource = new EventSource(`/api/live/events?clientId=${encodeURIComponent(clientId)}&token=${encodeURIComponent(token)}`);
  liveState.eventSource = eventSource;
  
  eventSource.onopen = () => {
    console.log('Live SSE connected');
    updateConnectionStatus('connected', 'Connected');
    liveState.reconnectAttempts = 0;
  };
  
  eventSource.onerror = (e) => {
    console.log('Live SSE error:', e);
    
    // Close the errored connection
    eventSource.close();
    liveState.eventSource = null;
    
    updateConnectionStatus('error', 'Connection lost');
    
    // Exponential backoff for reconnection
    const attempts = liveState.reconnectAttempts || 0;
    const delay = Math.min(5000 * Math.pow(2, attempts), 60000); // Max 60s
    liveState.reconnectAttempts = attempts + 1;
    
    console.log(`Will reconnect in ${delay/1000}s (attempt ${attempts + 1})`);
    
    setTimeout(() => {
      if (appState.currentTab === 'live') {
        connectLiveStream();
      }
    }, delay);
  };
  
  eventSource.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      handleLiveMessage(data);
    } catch (e) {
      console.error('Failed to parse SSE message:', e);
    }
  };
}

export function disconnectLiveStream(): void {
  if (liveState.eventSource) {
    liveState.eventSource.close();
    liveState.eventSource = null;
  }
  updateConnectionStatus('disconnected', 'Disconnected');
}

function handleLiveMessage(data: any): void {
  switch (data.type) {
    case 'connected':
      console.log('Live stream connected');
      break;
      
    case 'sessions':
      // Initial sessions list
      liveState.sessions.clear();
      if (data.sessions) {
        data.sessions.forEach((session: LiveSession) => {
          liveState.sessions.set(session.sessionId, {
            ...session,
            lastActivity: new Date(session.lastActivity)
          });
        });
      }
      renderLiveSessions();
      renderLiveFunnel();
      break;
      
    case 'event':
      handleLiveEvent(data.event);
      break;
      
    case 'session:new':
      liveState.sessions.set(data.session.sessionId, {
        ...data.session,
        lastActivity: new Date()
      });
      renderLiveSessions();
      break;
      
    case 'session:left':
      const session = liveState.sessions.get(data.session.sessionId);
      if (session) {
        session.lastActivity = new Date(0); // Mark as left
        renderLiveSessions();
      }
      break;
  }
}

function handleLiveEvent(event: LiveEvent): void {
  // Update session
  let session = liveState.sessions.get(event.sessionId);
  if (!session) {
    session = {
      sessionId: event.sessionId,
      visitorId: event.visitorId,
      clientId: event.clientId,
      funnelStage: event.funnelStage,
      currentPage: event.data?.pageUrl || '',
      productsViewed: 0,
      itemsInCart: 0,
      checkouts: 0,
      purchases: 0,
      orderValue: 0,
      lastActivity: new Date()
    };
    liveState.sessions.set(event.sessionId, session);
  }
  
  // Update session based on event
  session.lastActivity = new Date();
  session.funnelStage = Math.max(session.funnelStage, event.funnelStage);
  
  if (event.eventType === 'page_view') {
    session.currentPage = event.data?.pageUrl || '';
  }
  if (event.eventType === 'product_viewed') {
    session.productsViewed = (session.productsViewed || 0) + 1;
  }
  if (event.eventType === 'add_to_cart') {
    session.itemsInCart = (session.itemsInCart || 0) + (event.data?.quantity || 1);
  }
  if (event.eventType === 'checkout_initiated') {
    session.checkouts = (session.checkouts || 0) + 1;
  }
  if (event.eventType === 'purchase_completed') {
    session.purchases = (session.purchases || 0) + 1;
    session.orderValue = (session.orderValue || 0) + (event.data?.total || event.data?.orderValue || 0);
  }
  
  // Add to event feed
  liveState.events.unshift(event);
  if (liveState.events.length > liveState.maxEvents) {
    liveState.events = liveState.events.slice(0, liveState.maxEvents);
  }
  
  // Re-render
  renderLiveSessions();
  renderLiveEventFeed();
  renderLiveFunnel();
}

function updateConnectionStatus(status: 'connected' | 'connecting' | 'disconnected' | 'error', text: string): void {
  const dot = document.getElementById('liveStatusDot');
  const textEl = document.getElementById('liveStatusText');
  
  if (dot) {
    const colors: Record<string, string> = {
      connected: '#27ae60',
      connecting: '#f39c12',
      disconnected: '#bdc3c7',
      error: '#e74c3c'
    };
    dot.style.background = colors[status];
    if (status === 'connecting') {
      dot.classList.add('live-pulse');
    } else {
      dot.classList.remove('live-pulse');
    }
  }
  
  if (textEl) {
    textEl.textContent = text;
  }
}

function getSessionStatus(session: LiveSession): 'active' | 'idle' | 'left' {
  const now = Date.now();
  const lastActivity = session.lastActivity.getTime();
  
  if (lastActivity < now - liveState.sessionTimeout) return 'left';
  if (lastActivity < now - liveState.idleTimeout) return 'idle';
  return 'active';
}

export function renderLiveSessions(): void {
  const container = document.getElementById('liveSessionsList');
  if (!container) return;
  
  const sessions = Array.from(liveState.sessions.values())
    .filter(s => getSessionStatus(s) !== 'left')
    .sort((a, b) => b.lastActivity.getTime() - a.lastActivity.getTime());
  
  setText('liveSessionCount', `${sessions.length} active sessions`);
  
  if (sessions.length === 0) {
    container.innerHTML = `
      <div style="color: #7f8c8d; text-align: center; padding: 40px;">
        Waiting for visitors...
      </div>
    `;
    return;
  }
  
  const stageNames = ['', 'Browsing', 'Viewing Products', 'Added to Cart', 'Checkout', 'Purchased'];
  
  container.innerHTML = sessions.map(session => {
    const status = getSessionStatus(session);
    const statusEmoji = status === 'active' ? '🟢' : status === 'idle' ? '🟡' : '🔴';
    const timeSince = formatTimeSince(session.lastActivity);
    
    return `
      <div class="live-session-card status-${status}">
        <div class="live-session-header">
          <span class="live-session-id">${session.sessionId}</span>
          <span class="live-session-status ${status}">
            ${statusEmoji} ${status === 'active' ? 'Active' : status === 'idle' ? 'Idle' : 'Left'}
          </span>
        </div>
        <div class="live-session-funnel">
          ${[1,2,3,4,5].map(stage => `
            <div class="live-funnel-step ${session.funnelStage >= stage ? 'completed' : ''}"></div>
          `).join('')}
        </div>
        <div style="display: flex; justify-content: space-between; font-size: 12px; color: #7f8c8d;">
          <span>📍 ${stageNames[session.funnelStage] || 'Browsing'}</span>
          <span>🕐 ${timeSince}</span>
        </div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 8px; font-size: 12px;">
          <span>👁️ ${session.productsViewed || 0} viewed</span>
          <span>🛒 ${session.itemsInCart || 0} in cart</span>
          <span>💳 ${session.checkouts || 0} checkouts</span>
          <span>✅ ${session.purchases || 0} purchases${session.orderValue > 0 ? ` (${formatPrice(session.orderValue)})` : ''}</span>
        </div>
        <div class="live-session-page" title="${session.currentPage || ''}">
          📄 ${(session.currentPage || '').replace(/https?:\/\/[^\/]+/, '')}
        </div>
      </div>
    `;
  }).join('');
}

export function renderLiveEventFeed(): void {
  const container = document.getElementById('liveEventFeed');
  if (!container) return;
  
  if (liveState.events.length === 0) {
    container.innerHTML = `
      <div style="color: #7f8c8d; text-align: center; padding: 40px;">
        Waiting for events...
      </div>
    `;
    return;
  }
  
  container.innerHTML = liveState.events.slice(0, 50).map(event => {
    const time = new Date(event.timestamp).toLocaleTimeString('de-AT');
    const emoji = eventEmojis[event.eventType] || '📌';
    const details = getEventDetails(event);
    const sessionId = event.sessionId || '';
    
    return `
      <div class="live-event-item" style="flex-wrap: wrap;">
        <span class="live-event-time">${time}</span>
        <span class="live-event-type ${event.eventType}">${emoji} ${event.eventType}</span>
        <span class="live-event-details" title="${details}">${details}</span>
        ${sessionId ? `<span style="width: 100%; font-size: 10px; color: #999; font-family: monospace; margin-top: 4px; word-break: break-all;">📍 ${sessionId}</span>` : ''}
      </div>
    `;
  }).join('');
}

function getEventDetails(event: LiveEvent): string {
  const data = event.data || {};
  
  switch (event.eventType) {
    case 'page_view':
      return (data.pageUrl || '').replace(/https?:\/\/[^\/]+/, '') || data.pageTitle || '';
    case 'product_viewed':
      return data.productName || data.productId || '';
    case 'add_to_cart':
      return `${data.productName || data.productId || ''} (qty: ${data.quantity || 1})`;
    case 'cart_viewed':
      return `${data.itemCount || 0} items - ${formatPrice(data.cartValue || 0)}`;
    case 'checkout_initiated':
      return `Cart value: ${formatPrice(data.cartValue || 0)}`;
    case 'purchase_completed':
      return `Order: ${data.orderId || ''} - ${formatPrice(data.total || data.orderValue || 0)}`;
    case 'chatbot_opened':
      return 'Widget opened';
    case 'chatbot_closed':
      return 'Widget closed';
    case 'chat_started':
      return 'Conversation started';
    case 'chat_message':
      const msgPrefix = data.isUser ? '👤' : '🤖';
      const chatMsg = (data.message || '').substring(0, 100);
      return `${msgPrefix} ${chatMsg}${chatMsg.length >= 100 ? '...' : ''}`;
    case 'chat_message_sent':
      const userMsg = (data.content || data.message || '').substring(0, 80);
      return `${userMsg}${userMsg.length >= 80 ? '...' : ''}`;
    case 'chat_message_received':
      const botMsg = (data.content || data.message || '').substring(0, 80);
      return `${botMsg}${botMsg.length >= 80 ? '...' : ''}`;
    case 'product_recommendation_shown':
      return `🎁 ${data.productName || data.productId || 'Product'} (${data.context || 'recommended'})`;
    case 'search_no_results':
      return `No results for query`;
    case 'product_alternatives_shown':
      return `Alternatives suggested`;
    default:
      return JSON.stringify(data).substring(0, 50);
  }
}

export function renderLiveFunnel(): void {
  const container = document.getElementById('liveFunnelContainer');
  if (!container) return;
  
  const sessions = Array.from(liveState.sessions.values())
    .filter(s => getSessionStatus(s) !== 'left');
  
  if (sessions.length === 0) {
    container.innerHTML = `
      <div style="color: #7f8c8d; text-align: center; padding: 20px;">
        No active sessions
      </div>
    `;
    return;
  }
  
  // Aggregate actual metrics from all sessions
  const totalVisits = sessions.length;
  const totalProductViews = sessions.reduce((sum, s) => sum + (s.productsViewed || 0), 0);
  const totalCart = sessions.reduce((sum, s) => sum + (s.itemsInCart || 0), 0);
  const totalCheckouts = sessions.reduce((sum, s) => sum + (s.checkouts || 0), 0);
  const totalPurchases = sessions.reduce((sum, s) => sum + (s.purchases || 0), 0);
  
  const stageCounts = [totalVisits, totalProductViews, totalCart, totalCheckouts, totalPurchases];
  const stages = ['Visits', 'Product Views', 'Add to Cart', 'Checkout', 'Purchase'];
  const colors = ['#3498db', '#9b59b6', '#e67e22', '#1abc9c', '#27ae60'];
  const maxCount = Math.max(...stageCounts, 1);
  
  let html = '';
  stages.forEach((stage, i) => {
    const count = stageCounts[i];
    const widthPercent = (count / maxCount) * 100;
    const color = colors[i];
    
    html += `
      <div style="width: 100%; margin-bottom: 8px;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
          <span style="font-size: 13px;">${stage}</span>
          <span style="font-size: 13px; font-weight: 600;">${count}</span>
        </div>
        <div style="height: 20px; background: #ecf0f1; border-radius: 4px; overflow: hidden;">
          <div style="height: 100%; width: ${widthPercent}%; background: ${color}; transition: width 0.3s ease;"></div>
        </div>
      </div>
    `;
  });
  
  container.innerHTML = html;
}
